"use client"
import type React from "react"
import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, ChevronUp } from "lucide-react"

export const Card: React.FC<{
  title: string
  children?: React.ReactNode
  delay?: number
  isCollapsible?: boolean
  isInitiallyCollapsed?: boolean
}> = ({ title, children, delay = 0, isCollapsible = false, isInitiallyCollapsed = true }) => {
  const [isCollapsed, setIsCollapsed] = useState(isCollapsible ? isInitiallyCollapsed : false)

  const toggleCollapse = () => {
    if (isCollapsible) {
      setIsCollapsed(!isCollapsed)
    }
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0, y: 10 },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.4,
            delay: delay * 0.15,
          },
        },
      }}
      className="bg-white border border-gray-100 rounded-md overflow-hidden shadow-sm"
    >
      <div
        className={`p-3 bg-gray-50 text-gray-800 font-medium text-sm border-b border-gray-100 flex justify-between items-center ${isCollapsible ? "cursor-pointer hover:bg-gray-100 transition-colors" : ""}`}
        onClick={toggleCollapse}
      >
        {title}
        {isCollapsible &&
          (isCollapsed ? (
            <ChevronDown className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronUp className="w-5 h-5 text-gray-600" />
          ))}
      </div>
      <AnimatePresence initial={false}>
        {(!isCollapsible || !isCollapsed) && children && (
          <motion.div
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto", marginTop: 16, marginBottom: 16 },
              collapsed: { opacity: 0, height: 0, marginTop: 0, marginBottom: 0 },
            }}
            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="overflow-hidden"
          >
            <div className="p-4 pt-0">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
